import { type DayOfWeek, minuteOfDay } from "@offstreetparking/date-and-time";
import { DateTime } from "luxon";

export const DAYS_OF_WEEK = [
	"MONDAY",
	"TUESDAY",
	"WEDNESDAY",
	"THURSDAY",
	"FRIDAY",
	"SATURDAY",
	"SUNDAY",
] as const satisfies readonly DayOfWeek[];

export type IntervalSchedule = {
	day: DayOfWeek;
	startTime: string;
	endTime: string;
};

export const schedulesOnDate = <S extends { day: string }>({
	dateTimeISO,
	timezone,
	schedules,
}: {
	dateTimeISO: string;
	timezone: string;
	schedules: readonly S[] | undefined;
}): S[] => {
	const day = DAYS_OF_WEEK[DateTime.fromISO(dateTimeISO, { zone: timezone }).weekday - 1];

	return day ? (schedules?.filter((schedule) => schedule.day === day) ?? []) : [];
};

export const SCHEDULE_HORIZON_DAYS = 7;

export type ScheduledIntervalTrim<S> = {
	startDateTimeISO: string;
	schedules: S[];
};

export const trimScheduledIntervalToLiveWindows = <S extends { day: string; startTime: string; endTime: string }>({
	startDateTimeISO,
	endDateTimeISO,
	schedules,
	timezone,
	now,
}: {
	startDateTimeISO: string;
	endDateTimeISO: string;
	schedules: readonly S[];
	timezone: string;
	now: DateTime;
}): ScheduledIntervalTrim<S> | undefined => {
	const start = DateTime.fromISO(startDateTimeISO, { zone: timezone });
	const end = DateTime.fromISO(endDateTimeISO, { zone: timezone });
	const nowInZone = now.setZone(timezone);

	if (nowInZone <= start) {
		return { startDateTimeISO, schedules: [...schedules] };
	}
	if (nowInZone >= end) {
		return undefined;
	}

	const firstDay = nowInZone.startOf("day");
	const horizon = firstDay.plus({ days: SCHEDULE_HORIZON_DAYS });
	const endDay = end.startOf("day");
	const lastDay = endDay < horizon ? endDay : horizon;

	const liveSchedules = new Set<S>();
	let earliestStart: DateTime | undefined;

	for (let day = firstDay; day <= lastDay; day = day.plus({ days: 1 })) {
		const dayISO = day.toISO();
		if (!dayISO) {
			continue;
		}

		for (const schedule of schedulesOnDate({ dateTimeISO: dayISO, timezone, schedules })) {
			const fromMinute = minuteOfDay(schedule.startTime);
			const toMinute = minuteOfDay(schedule.endTime);
			if (toMinute <= fromMinute) {
				continue;
			}

			const windowStart = day.set({
				hour: Math.floor(fromMinute / 60),
				minute: fromMinute % 60,
				second: 0,
				millisecond: 0,
			});
			const windowEnd = day.set({
				hour: Math.floor(toMinute / 60),
				minute: toMinute % 60,
				second: 0,
				millisecond: 0,
			});
			const clampedStart = windowStart > start ? windowStart : start;
			const clampedEnd = windowEnd < end ? windowEnd : end;
			if (clampedEnd <= clampedStart || clampedEnd <= nowInZone) {
				continue;
			}

			liveSchedules.add(schedule);
			const liveStart = clampedStart > nowInZone ? clampedStart : nowInZone;
			if (!earliestStart || liveStart < earliestStart) {
				earliestStart = liveStart;
			}
		}
	}

	if (!earliestStart) {
		return undefined;
	}

	return {
		startDateTimeISO: earliestStart.toISO() ?? startDateTimeISO,
		schedules: schedules.filter((schedule) => liveSchedules.has(schedule)),
	};
};

export const applyScheduleToDateTime = <T extends string | null | undefined>({
	dateTimeISO,
	timezone,
	schedules,
	boundary,
}: {
	dateTimeISO: T;
	timezone: string | null | undefined;
	schedules: readonly { day: string; startTime: string; endTime: string }[] | undefined;
	boundary: "start" | "end";
}): string | T => {
	if (!dateTimeISO || !timezone) {
		return dateTimeISO;
	}

	const matching = schedulesOnDate({ dateTimeISO, timezone, schedules });
	if (matching.length === 0) {
		return dateTimeISO;
	}

	const time =
		boundary === "start"
			? matching.reduce((earliest, schedule) => (schedule.startTime < earliest.startTime ? schedule : earliest))
					.startTime
			: matching.reduce((latest, schedule) => (schedule.endTime > latest.endTime ? schedule : latest)).endTime;
	const [hour = 0, minute = 0] = time.split(":").map(Number);

	return (
		DateTime.fromISO(dateTimeISO, { zone: timezone }).set({ hour, minute, second: 0, millisecond: 0 }).toISO() ??
		dateTimeISO
	);
};
